<template>
    <th
        class="th-aips-sort table-aips-header"
        :class="{ 'sort-on': sortDirection }"
        @click="toggle()"
        @mouseenter="hovering = true"
        @mouseleave="hovering = false"
    >
        <slot></slot>

        <div v-if="showSortIcons || sortDirection !== 0" class="arrow-holder">
            <div
                v-if="sortDirection === 0"
                class="arrow up"
                :data-cy="`header.sort-none`"
            ></div>
            <div v-if="sortDirection === 0" class="arrow down"></div>
            <div
                v-if="sortDirection === 1"
                class="arrow up separate"
                :data-cy="`header.sort-asc`"
            ></div>
            <div
                v-if="sortDirection === -1"
                class="arrow down separate"
                :data-cy="`header.sort-desc`"
            ></div>
        </div>
        <sup
            v-if="showPriorityIcon"
            class="sup"
            :data-cy="`header.sort-priority`"
        >
            {{ sortPriority }}
        </sup>
    </th>
</template>

<script setup>
import { debounce } from 'lodash-es';
import { ref, computed, watch } from 'vue';
const emit = defineEmits(['onSort']);
const props = defineProps({
    columnName: {
        type: String,
        required: true,
    },
    sortState: {
        type: Array,
        default: () => [],
    },
});

const sortDirection = ref(0);
const sortPriority = ref(0);
const advancedSort = ref(false);
const hovering = ref(false);

const detail = computed(() => {
    return {
        sortDirection: sortDirection.value,
        sortColumn: props.columnName,
        sortPriority: sortPriority.value,
    };
});
const showPriorityIcon = computed(() => {
    return advancedSort.value && sortPriority.value > 0;
});
const showSortIcons = computed(() => {
    return (
        (sortDirection.value === 0 && hovering.value) ||
        (sortDirection.value === 0 && advancedSort.value)
    );
});

watch(
    () => props.sortState,
    () => {
        handleSorted();
    },
    { deep: true }
);

const toggle = debounce(() => {
    if (sortDirection.value === 1) {
        sortDirection.value = -1;
    } else if (sortDirection.value === 0) {
        sortDirection.value = 1;
    } else {
        sortDirection.value = 0;
    }

    emit('onSort', detail.value);
}, 300);

const handleSorted = () => {
    let column = null;

    for (let col of props.sortState) {
        if (col.sortColumn === props.columnName) {
            column = col;
            break;
        }
    }
    if (column && column.sortPriority !== null) {
        sortPriority.value = column.sortPriority;
        sortDirection.value = column.sortDirection;
    } else if (!column) {
        sortDirection.value = 0;
        sortPriority.value = 0;
    }
};

const advancedToggled = (advancedSort) => {
    advancedSort.value = advancedSort;
    sortDirection.value = 0;
    sortPriority.value = 0;
};
</script>

<script>
export default {
    name: 'ThAipsSort',
};
</script>

<style lang="scss">
.th-aips-sort {
    position: relative;

    .sup {
        vertical-align: super;
        font-size: 80%;
        color: $tealDark;
    }

    i {
        width: 15px;
        height: 15px;
        display: inline-block;
    }

    .arrow-holder {
        width: 13px;
        display: inline-flex;
        flex-wrap: wrap;

        position: relative;
        top: -4px;
        right: -8px;
    }

    .arrow {
        height: 2px;
        width: 10px;
        position: relative;
        align-self: flex-end;
        margin-left: auto;

        &.up {
            transform: rotate(180deg);
            top: -5px;

            &.separate {
                top: 0;
            }
        }

        &.down {
            bottom: -5px;

            &.separate {
                top: 0;
            }
        }
    }

    .arrow::before,
    .arrow::after {
        content: '';
        position: absolute;
        bottom: 0;
        width: 100%;
        height: 100%;
        transition: all 0.3s;
    }
    .arrow::before {
        left: -3px;
        transform: rotate(45deg);
        background-color: $peacock;
    }
    .arrow::after {
        left: 3px;
        transform: rotate(-45deg);
        background-color: $peacock;
    }

    &.float-icon {
        .arrow-holder {
            position: absolute;
            top: 50%;
            right: 15px;
        }
    }
}
</style>
